"use client"

/**
 * Floating "watch an ad, get ad-free time" entry point. Mirrors
 * AdTriggerOverlay's floating admin button (same circular shape) but for
 * real visitors, with a money icon instead of a lightning bolt.
 *
 * Bottom-center, at bottom-16 (64px). Was bottom-24 (96px), which read as
 * too high. Not lower than 64px: booru-browser.tsx's pagination pill owns
 * bottom-4/center and is ~52-60px tall in "pagination" loading mode, so
 * anything below this starts overlapping it. A corner placement was tried
 * and rejected — it collided with the side-rail buttons instead.
 *
 * Home-page Browse view only (see useIsHomeBrowseView below) — deliberate,
 * not a placement gap: it shouldn't follow the visitor around the rest of
 * the app, including the other views that live on the "/" route.
 *
 * Enable/disable, minutes granted, and the per-day cap are all admin-
 * controlled via /api/admin/skip-ads-config — see the `config` state below.
 * Previously all three were hardcoded with no admin surface at all.
 */

import { useEffect, useState } from "react"
import { useIsHomeBrowseView } from "@/lib/ad-placement"
import { MonitorPlay, } from "@/lib/icons"
import { SkipAdsModal } from "@/components/ads/SkipAdsModal"
import { canEarnSkipAds, resetSkipAdsGrant } from "@/lib/skip-ads"
import { useAdStore } from "@/lib/ad-store"
import { useAuthStore } from "@/lib/auth-store"
import { isAdTestModeOn } from "@/lib/ad-cooldown"
import { isGlobalAdsOff } from "@/lib/global-ads-off"
import { useTranslations } from "next-intl"

const ELIGIBILITY_POLL_MS = 5000
const ADMIN_ROLES = ["admin", "super_admin"]

// Fallback matches the DB default in migration 20261208000000_skip_ads_config —
// used only until the real config loads, and if that fetch ever fails.
const DEFAULT_CONFIG = { enabled: true, grant_minutes: 30, max_grants_per_day: 1 }

export function SkipAdsButton() {
  const t = useTranslations("skipAdsButton")
  // The home page's Browse view only. Gooncave, Game Arena, Editors and
  // Collections are views on the same "/" route (?view=), so a pathname check
  // alone let the button leak onto them.
  const onHomeBrowse = useIsHomeBrowseView()
  const [open, setOpen] = useState(false)
  const [eligible, setEligible] = useState(false)
  const [config, setConfig] = useState(DEFAULT_CONFIG)
  const { isPremiumUser } = useAdStore()
  const { profile } = useAuthStore()
  // An admin/super_admin account testing this button was previously reliant
  // on the Force Ads localStorage flag (isAdTestModeOn()) staying set across
  // navigations — reported live as "works once, then disappears for a
  // while," i.e. the flag silently not being on in whatever tab/moment was
  // actually being tested. Checking the real profile role directly removes
  // that dependency entirely: an admin account always bypasses the premium
  // gate and the once-per-day cap below, full stop, no toggle required.
  const isAdminTester = !!profile?.role && ADMIN_ROLES.includes(profile.role)
  // Same escape hatch as every other ad component this session — without it,
  // admin/super_admin accounts (always isPremiumUser: true) could never see
  // or test this button outside Force Ads mode. A genuinely premium/paying
  // user already has zero ads sitewide, so they don't need this reward at
  // all — hidden for them, not just gated on click.
  const effectivePremiumUser = (isPremiumUser || isGlobalAdsOff()) && !isAdminTester && !isAdTestModeOn()

  // Admin-controlled via /api/admin/skip-ads-config (enable/disable, minutes
  // granted, per-day cap) — was hardcoded (always on, 30 minutes, once/day)
  // with no admin surface at all. Public GET, so this loads for every
  // visitor, not just staff.
  useEffect(() => {
    let cancelled = false
    fetch("/api/admin/skip-ads-config")
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!cancelled && d?.config) setConfig(d.config)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  // Per-day cap (see lib/skip-ads.ts) — reported live that a persistent
  // "Ad-free for Xm" badge sitting mid-screen felt like UI bloat the site
  // didn't need. Simplified to the bluntest version: while a grant is active
  // OR today's admin-configured allowance is already spent, this renders
  // nothing at all rather than a standing indicator. The button just
  // reappears on its own tomorrow (or immediately, if the cap is more than 1).
  //
  // For an admin tester (or Force Ads test mode), skip the real-user cap
  // entirely — always eligible, and resetSkipAdsGrant() below clears the
  // grant right after it's earned so the same tester can click through it
  // repeatedly in one session instead of it genuinely locking out for the
  // rest of the day.
  useEffect(() => {
    const check = () =>
      setEligible(isAdminTester || isAdTestModeOn() || canEarnSkipAds(config.max_grants_per_day))
    check()
    const interval = setInterval(check, ELIGIBILITY_POLL_MS)
    return () => clearInterval(interval)
  }, [isAdminTester, config.max_grants_per_day])

  if (!onHomeBrowse || effectivePremiumUser || !eligible || !config.enabled) return null

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-16 left-1/2 -translate-x-1/2 z-40 h-10 w-10 rounded-full bg-green-600 hover:bg-green-500 text-white shadow-lg flex items-center justify-center hover:scale-105 transition-transform"
        aria-label={t("watchAnAdToGoAdFreeForMinutes", { minutes: config.grant_minutes })}
        title={t("watchAnAdToGoAdFreeForMinutesUpToNPerDay", {
          minutes: config.grant_minutes,
          maxPerDay: config.max_grants_per_day,
        })}
      >
        <MonitorPlay className="h-4 w-4" />
      </button>

      {open && (
        <SkipAdsModal
          grantMinutes={config.grant_minutes}
          onClose={() => setOpen(false)}
          onGranted={() => {
            if (isAdminTester || isAdTestModeOn()) resetSkipAdsGrant()
            setEligible(isAdminTester || isAdTestModeOn() || canEarnSkipAds(config.max_grants_per_day))
          }}
        />
      )}
    </>
  )
}
